import { Connection, PublicKey } from '@solana/web3.js'

import { getProgramReadOnly, getUserInfoPDA, getGlobalConfigPDA, getPrizePoolPDA } from './anchorClient'

/**
 * UserInfo account data from on-chain
 */
export interface UserInfoAccount {
  balance: bigint // User's chest credits (total from burning + deposits)
  bump: number
}

/**
 * GlobalConfig account data from on-chain
 */
export interface GlobalConfigAccount {
  paused: boolean
  teamFeeReceiver: PublicKey
  lotteryPoolFeeReceiver: PublicKey
  configAuthority: PublicKey
  signerAuthority: PublicKey
  tokenFeesBps: number
  nftFeesBps: number
  openChestTeamFeesBps: number
  openChestLotteryPoolFeeBps: number
  probabilities: Probability[]
  chestPrice: bigint
  bump: number
}

export interface Probability {
  chance: bigint
  rewardAmount: bigint
  isPoint: boolean
}

/**
 * PrizePool account data from on-chain
 */
export interface PrizePoolAccount {
  totalRemainingReward: bigint
  totalChests: bigint
  bump: number
}

/**
 * Fetch UserInfo account for a wallet
 * Returns null if account doesn't exist yet
 *
 * @deprecated Use backend API `/user/progress` instead for fetching user data.
 * Frontend should not directly query blockchain accounts.
 * This function will be removed in future versions.
 */
export async function getUserInfoAccount(
  connection: Connection,
  walletAddress: PublicKey
): Promise<UserInfoAccount | null> {
  try {
    const program = getProgramReadOnly(connection)
    const [userInfoPDA] = getUserInfoPDA(walletAddress)

    console.log('🔍 [getUserInfoAccount] Fetching account for wallet:', walletAddress.toString())
    console.log('🔍 [getUserInfoAccount] UserInfo PDA:', userInfoPDA.toString())

    // Single RPC call to fetch the account data (optimized from 3 calls)
    const userInfo = await (program.account as any).userInfo.fetch(userInfoPDA)

    if (!userInfo) {
      console.log('📭 UserInfo account does not exist yet for:', walletAddress.toString())
      return null
    }

    console.log('📊 [getUserInfoAccount] Decoded userInfo:', {
      balance: userInfo.balance.toString(),
      bump: userInfo.bump,
    })

    return {
      balance: BigInt(userInfo.balance.toString()),
      bump: userInfo.bump,
    }
  } catch (error) {
    console.error('❌ [getUserInfoAccount] Failed to fetch UserInfo account:', error)
    return null
  }
}

/**
 * Fetch GlobalConfig account
 *
 * @deprecated Use backend API `/config/global` instead for fetching configuration.
 * Frontend should not directly query blockchain accounts.
 * This function will be removed in future versions.
 */
export async function getGlobalConfigAccount(
  connection: Connection
): Promise<GlobalConfigAccount | null> {
  try {
    const program = getProgramReadOnly(connection)
    const [globalConfigPDA] = getGlobalConfigPDA()

    const globalConfig = await (program.account as any).globalConfig.fetch(globalConfigPDA)

    return {
      paused: globalConfig.paused,
      teamFeeReceiver: globalConfig.teamFeeReceiver as PublicKey,
      lotteryPoolFeeReceiver: globalConfig.lotteryPoolFeeReceiver as PublicKey,
      configAuthority: globalConfig.configAuthority as PublicKey,
      signerAuthority: globalConfig.signerAuthority as PublicKey,
      tokenFeesBps: globalConfig.tokenFeesBps,
      nftFeesBps: globalConfig.nftFeesBps,
      openChestTeamFeesBps: globalConfig.openChestTeamFeesBps,
      openChestLotteryPoolFeeBps: globalConfig.openChestLotteryPoolFeeBps,
      probabilities: globalConfig.probabilities.map((p: any) => ({
        chance: BigInt(p.chance.toString()),
        rewardAmount: BigInt(p.rewardAmount.toString()),
        isPoint: p.isPoint,
      })),
      chestPrice: BigInt(globalConfig.chestPrice.toString()),
      bump: globalConfig.bump,
    }
  } catch (error) {
    console.error('Failed to fetch GlobalConfig account:', error)
    return null
  }
}

/**
 * Fetch PrizePool account
 *
 * @deprecated Use backend API `/config/prize-pool` instead for fetching prize pool data.
 * Frontend should not directly query blockchain accounts.
 * This function will be removed in future versions.
 */
export async function getPrizePoolAccount(
  connection: Connection
): Promise<PrizePoolAccount | null> {
  try {
    const program = getProgramReadOnly(connection)
    const [prizePoolPDA] = getPrizePoolPDA()

    const prizePool = await (program.account as any).prizePool.fetch(prizePoolPDA)

    return {
      totalRemainingReward: BigInt(prizePool.totalRemainingReward.toString()),
      totalChests: BigInt(prizePool.totalChests.toString()),
      bump: prizePool.bump,
    }
  } catch (error) {
    console.error('Failed to fetch PrizePool account:', error)
    return null
  }
}

/**
 * Calculate how many chests a user can open
 *
 * @deprecated Backend API `/user/progress` should provide available_chests field.
 * Frontend should not calculate business logic locally.
 * This function will be removed in future versions.
 */
export function calculateAvailableChests(
  userBalance: bigint,
  chestPrice: bigint
): number {
  if (chestPrice === BigInt(0)) return 0
  return Number(userBalance / chestPrice)
}

/**
 * Format account data for display
 */
export function formatUserInfo(userInfo: UserInfoAccount, chestPrice: bigint): string {
  const availableChests = calculateAvailableChests(userInfo.balance, chestPrice)
  const balanceSOL = Number(userInfo.balance) / 1e9
  const chestPriceSOL = Number(chestPrice) / 1e9

  return `
Balance: ${balanceSOL.toFixed(6)} SOL
Available Chests: ${availableChests}
Chest Price: ${chestPriceSOL.toFixed(6)} SOL
  `.trim()
}